import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import FileSaver from 'file-saver';

import FileUpload from 'b2b-ui-components-lib/lib/components/fileUpload';

import {
  bulkUploadAddressResolution,
  bulkUploadCart,
  downloadErrorSheet,
  downloadSummaryErrorSheet,
  orderBulkUpload,
  siteReadinessBulkUpload
} from 'home/view/manage-feasibility/services';

import { downloadFileURI, validateFile, getFileName } from 'home/view/manage-feasibility/utils';

import { setInSessionStorage, getFromSessionStorage, trackAnalytics } from 'common/utils/app.utils';

import { MULTI_SITE_MODULE_ID, AUTH_TOKEN, LEAD_ID, CART_ID, ERROR_TOAST, DEFAULT_TOAST_MSG, FILE_UPLOAD_PAGES, SUMMARY_AUTH_TOKEN } from 'home/view/manage-feasibility/constants';

import './index.scss';
import { getCurrentLead, getCurrentOrder } from 'tokenGeneration/lead.util';

const defaultFileUploadProps = {
  name: 'multi-site-file-upload',
  label: 'Click or drop file to upload',
  placeholder: '',
  helpTexts: ['Supported file format is .xlsx • File size must be maximum 1Mb'],
  accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
  required: true
};

const UploadComponent = ({
  heading = 'Upload Excel Edited File',
  subHeading = 'Upload the Excel file of edited sites here. Please note all changes as per the uploaded file will be revised and updated on the system. ',
  setToast = () => {},
  currentPage = '',
  onUploadCb,
  reset = false,
  setReset,
  isDrawerModal = false
}) => {
  const [uploadFileProps, setUploadFileProps] = useState(defaultFileUploadProps);

  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [progressValue, setProgressValue] = useState(0);

  useEffect(() => {
    if (reset) {
      setUploadFileProps(defaultFileUploadProps);
      setIsFileUploaded(false);
      setUploadedFiles([]);
      setReset(false);
    }
  }, [reset]);

  useEffect(() => {
    onUploadCb(uploadedFiles, isFileUploaded);
  }, [uploadedFiles, isFileUploaded]);

  const areFilesValid = (files) => {
    let isValid = true;
    if (Array.isArray(files) && files.length > 0) {
      files.forEach((file) => {
        const { name: fileName, size, type } = file;
        const { isTypeValid, isSizeValid } = validateFile(type, size);
        if (!isTypeValid) {
          isValid = false;
          const updatedFileList = files.filter(({ name }) => name !== fileName);
          setIsFileUploaded(false);
          setUploadedFiles(updatedFileList);

          setUploadFileProps((uploadFileProps) => ({
            ...uploadFileProps,
            helpTexts: [],
            errors: [<p style={{ color: '#ED4F42' }}>Supported file format is .xlsx</p>],
            errorClass: 'errorMsg',
            showAllErrors: true,
            touched: true,
            renderUploadedFiles: [],
            key: Date.now()
          }));

          setToast(true, 'Invalid file format. Try to re-upload the correct format', ERROR_TOAST);

          return false;
        }

        if (!isSizeValid) {
          isValid = false;
          const updatedFileList = files.filter(({ name }) => name !== fileName);
          setIsFileUploaded(false);
          setUploadedFiles(updatedFileList);

          setUploadFileProps((uploadFileProps) => ({
            ...uploadFileProps,
            helpTexts: [],
            errors: [<p style={{ color: '#ED4F42' }}> File size must be maximum 1Mb </p>],
            errorClass: 'errorMsg',
            showAllErrors: true,
            touched: true,
            renderUploadedFiles: [],
            key: Date.now()
          }));

          setToast(true, 'Invalid file size. File size must be maximum 1Mb', ERROR_TOAST);
          return false;
        }
      });
    } else {
      isValid = false;

      setIsFileUploaded(false);
      setUploadedFiles([]);

      const fileProps = {
        ...defaultFileUploadProps,
        key: Date.now()
      };
      setUploadFileProps(fileProps);
    }
    return isValid;
  };

  const fileUploadApiMap = (file) => {
    let getUploadData;
    const leadAuthToken = getFromSessionStorage(MULTI_SITE_MODULE_ID, AUTH_TOKEN);

    const orderAuthToken = getFromSessionStorage(MULTI_SITE_MODULE_ID, SUMMARY_AUTH_TOKEN);

    switch (currentPage) {
      case FILE_UPLOAD_PAGES.SAMPLE_DOWNLOAD: {
        const formData = new FormData();
        formData.append('file', file);

        getUploadData = bulkUploadAddressResolution.bind(null, formData, leadAuthToken, onUploadProgress, FILE_UPLOAD_PAGES.SAMPLE_DOWNLOAD);

        break;
      }
      case FILE_UPLOAD_PAGES.ADDRESS_RESOLUTION: {
        const formData = new FormData();
        formData.append('file', file);

        getUploadData = bulkUploadAddressResolution.bind(null, formData, leadAuthToken, onUploadProgress, FILE_UPLOAD_PAGES.ADDRESS_RESOLUTION);

        break;
      }
      case FILE_UPLOAD_PAGES.CART_FEASIBILITY: {
        let cartId = getFromSessionStorage(MULTI_SITE_MODULE_ID, CART_ID);

        const formData = new FormData();
        formData.append('file', file);

        getUploadData = bulkUploadCart.bind(null, formData, leadAuthToken, cartId, onUploadProgress);

        break;
      }
      case FILE_UPLOAD_PAGES.ORDER_SUMMARY: {
        const formData = new FormData();
        formData.append('file', file);

        getUploadData = orderBulkUpload.bind(null, formData, orderAuthToken, onUploadProgress);

        break;
      }
      case FILE_UPLOAD_PAGES.SITE_READINESS: {
        const formData = new FormData();
        formData.append('file', file);

        getUploadData = siteReadinessBulkUpload.bind(null, formData, orderAuthToken, onUploadProgress);

        break;
      }
      default: {
        getUploadData = () => {};
      }
    }

    return getUploadData;
  };

  const getErrSheetApi = (errorSheetUrl) => {
    const isSummaryPage = currentPage === FILE_UPLOAD_PAGES.ORDER_SUMMARY || currentPage === FILE_UPLOAD_PAGES.SITE_READINESS;

    const leadAuthToken = getFromSessionStorage(MULTI_SITE_MODULE_ID, AUTH_TOKEN);

    const leadId = getFromSessionStorage(MULTI_SITE_MODULE_ID, LEAD_ID);

    const orderAuthToken = getFromSessionStorage(MULTI_SITE_MODULE_ID, SUMMARY_AUTH_TOKEN);

    if (isSummaryPage) {
      return downloadSummaryErrorSheet(errorSheetUrl, orderAuthToken);
    }

    return downloadErrorSheet(errorSheetUrl, leadAuthToken, leadId);
  };

  const getErrFileDefaultName = () => {
    let errFileName = '';

    switch (currentPage) {
      case FILE_UPLOAD_PAGES.SAMPLE_DOWNLOAD: {
        errFileName = 'Error_Sample_Bulk_Upload_Feasibility_DI.xlsx';
        break;
      }
      case FILE_UPLOAD_PAGES.ADDRESS_RESOLUTION: {
        errFileName = 'Error_Bulk_Address_DI.xlsx';
        break;
      }
      case FILE_UPLOAD_PAGES.CART_FEASIBILITY: {
        errFileName = 'Error_Cart_Bulk_Upload_DI.xlsx';
        break;
      }
      case FILE_UPLOAD_PAGES.ORDER_SUMMARY: {
        errFileName = 'Error_Order_Summary_Upload_DI.xlsx';

        break;
      }
      case FILE_UPLOAD_PAGES.SITE_READINESS: {
        errFileName = 'Error_Order_Summary_Upload_DI.xlsx';

        break;
      }
      default: {
        errFileName = '';
      }
    }

    return errFileName;
  };

  const downloadErrFile = async (errorSheetUrl) => {
    try {
      const { res, err } = await getErrSheetApi(errorSheetUrl);
      if (res) {
        const { data = {} } = res;
        const fileName = getFileName(res, getErrFileDefaultName());
        downloadFileURI(data, fileName);
      } else if (err) {
        const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

        setToast(true, errorMsg, ERROR_TOAST);

        throw new Error(errorMsg);
      }
    } catch (err) {
      const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

      setToast(true, errorMsg, ERROR_TOAST);

      throw new Error(errorMsg);
    }
  };

  const onUploadProgress = (progressEvent) => {
    const loadingPercentage = (progressEvent.loaded / progressEvent.total) * 100;
    setProgressValue(loadingPercentage - 5);
  };

  const sendAnalytics = () => {
    const order = getCurrentOrder();
    const lead = getCurrentLead();
    trackAnalytics({
      eVar9: `b2b|${order?.serviceType}|buy|confirm installations|site readiness widget|edit with excel page|upload`,
      prop0: `b2b|${order?.lead?.customer?.segment}|${order?.serviceType}`,
      prop15: order?.customerEmail || '', //email address
      prop17: `${order?.lead?.customer?.authorizedSignatory?.firstName || ''} ${order?.lead?.customer?.authorizedSignatory?.lastName || ''}`, //full name
      prop14: order?.lead?.customer?.authorizedSignatory?.phoneNumber || '', //phone number
      prop35: order?.companyAddress?.officeName || '',
      prop39: order?.lead?.customer?.segment || '',
      prop40: order?.serviceType || '',
      prop37: order?.orderType || '',
      // prop41=<type of site>
      // Prop43=<no. of sites>
      // prop21=<feasibility request id>
      // Prop23=<link id>
      prop9: order?.companyAddress?.pincode || '',
      prop36: order?.lead?.customer?.authorizedSignatory?.companyName || '',
      prop20: order?.companyAddress?.addressLine1 || '',
      // Prop26=<bandwidth>
      // Prop28=<site type>
      prop10: order?.orderId || '',
      // Prop29=<media type>
      // Prop22=<contract period>
      prop18: order?.lead?.planDetail?.totalPrice || '',
      prop1: order?.purchaseOrder?.poNumber || '',
      prop24: order?.documents?.PURCHASE_ORDER?.PO_DOCUMENT || '',
      prop25: order?.purchaseOrder?.purchaseOrderDate || '',
      prop2: order?.purchaseOrder?.poAmount || '',
      prop3: order?.billingType || '',
      prop4: order?.billingCycle || '',
      prop5: order?.lead?.customer?.panNumber || '', //<account number>
      prop16: order?.companyAddress?.city || '',
      // Prop7=<full name of site contact details>
      // Prop8=<mobile number of site contact details>
      // Prop9=<email address of site contact details>
      // Prop37=<customer need by date>
      prop38: order?.fiscalCode?.id || ''
      // Prop11=<full name of CPM>
      // Prop12=<mobile number of CPM>
      // Prop13=<email address of CPM>
    });
  };

  const onUploadChange = (files) => {
    if (!files) {
      setUploadFileProps({
        ...uploadFileProps,
        value: [],
        key: Date.now()
      });

      setIsFileUploaded(false);
      setUploadedFiles([]);
      return false;
    }

    const isValid = areFilesValid(Array.from(files));

    if (isValid) {
      Array.from(files).forEach(async (file) => {
        const { name: fileName } = file;

        if (fileUploadApiMap && typeof fileUploadApiMap === 'function') {
          try {
            const postFn = fileUploadApiMap(file);
            const { res, err } = await postFn();

            if (res) {
              const { data: { success = false, result: { leadId = '', errorSheetUrl = '', error = false } = {} } = {} } = res;

              setInSessionStorage(MULTI_SITE_MODULE_ID, LEAD_ID, leadId);

              if (success) {
                const updatedFileList = Array.from(files).filter(({ name }) => name === fileName);

                setProgressValue(100);
                setTimeout(() => setProgressValue(0));
                setIsFileUploaded(true);
                setUploadedFiles((uploadedFiles) => [...uploadedFiles, ...updatedFileList]);
                setUploadFileProps((uploadFileProps) => ({
                  ...uploadFileProps,
                  helpTexts: [],
                  errors: [],
                  renderUploadedFiles: displayUploadedFiles(Array.from(updatedFileList)),
                  key: Date.now()
                }));
              } else if (error && errorSheetUrl) {
                const getErrMsg = () => {
                  return (
                    <span className="toast-error-container">
                      Errors found in the file.{' '}
                      <a href="javascript:void(0)" className="error-style" role="button" tabIndex="0" onClick={() => downloadErrFile(errorSheetUrl)}>
                        Download error file
                      </a>
                    </span>
                  );
                };

                const updatedFileList = Array.from(files).filter(({ name }) => name !== fileName);
                setTimeout(() => setProgressValue(0));
                setToast(true, getErrMsg(), ERROR_TOAST);
                setIsFileUploaded(false);
                setUploadedFiles(updatedFileList);

                setUploadFileProps((uploadFileProps) => ({
                  ...uploadFileProps,
                  helpTexts: [],
                  errors: [],
                  renderUploadedFiles: getErrMsg(),
                  key: Date.now()
                }));
              }
            } else if (err) {
              const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

              const updatedFileList = Array.from(files).filter(({ name }) => name !== fileName);

              setTimeout(() => setProgressValue(0));
              setIsFileUploaded(false);
              setUploadedFiles(updatedFileList);

              setToast(true, errorMsg, ERROR_TOAST);

              setUploadFileProps((uploadFileProps) => ({
                ...uploadFileProps,
                errors: [],
                renderUploadedFiles: [],
                key: Date.now()
              }));

              throw new Error(errorMsg);
            }
          } catch (err) {
            const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

            const updatedFileList = Array.from(files).filter(({ name }) => name !== fileName);

            setTimeout(() => setProgressValue(0));
            setIsFileUploaded(false);
            setUploadedFiles(updatedFileList);

            setToast(true, errorMsg, ERROR_TOAST);

            setUploadFileProps((uploadFileProps) => ({
              ...uploadFileProps,
              errors: [],
              renderUploadedFiles: [],
              key: Date.now()
            }));

            throw new Error(errorMsg);
          }
        }
      });
    }
  };

  const displayUploadedFiles = (files) => {
    if (Array.isArray(files)) {
      return files.map((file, index) => {
        const { name } = file;
        return (
          <div key={index + name}>
            <span className="fileName" title={name} onClick={downloadFile.bind(null, name, file)}>
              {name}
            </span>
            <span className="cross_icon" onClick={removeFiles.bind(null, name, files)}></span>
          </div>
        );
      });
    }
  };

  const getFileDefaultName = () => {
    let defaultFileName = '';

    switch (currentPage) {
      case FILE_UPLOAD_PAGES.SAMPLE_DOWNLOAD: {
        defaultFileName = 'Sample_Bulk_Upload_Feasibility_DI.xlsx';
        break;
      }
      case FILE_UPLOAD_PAGES.ADDRESS_RESOLUTION: {
        defaultFileName = 'Edit_Bulk_Address_DI.xlsx';
        break;
      }
      case FILE_UPLOAD_PAGES.CART_FEASIBILITY: {
        defaultFileName = 'Edit_Cart_Bulk_Upload_DI.xlsx';
        break;
      }
      case FILE_UPLOAD_PAGES.ORDER_SUMMARY: {
        defaultFileName = 'Edit_Order_Summary_Upload_DI.xlsx';

        break;
      }
      case FILE_UPLOAD_PAGES.SITE_READINESS: {
        defaultFileName = 'Edit_Order_Summary_Upload_DI.xlsx';

        break;
      }
      default: {
        defaultFileName = '';
      }
    }

    return defaultFileName;
  };

  const downloadFile = (name, file) => {
    FileSaver.saveAs(file, name || getFileDefaultName());
  };

  const removeFiles = (fileName, fileList = []) => {
    const updatedFileList = fileList.filter(({ name }) => name !== fileName);
    if (updatedFileList.length === 0) {
      setUploadFileProps({
        ...uploadFileProps,
        errors: [],
        renderUploadedFiles: [],
        key: Date.now()
      });

      setIsFileUploaded(false);
      setUploadedFiles([]);

      return false;
    }

    setUploadedFiles(updatedFileList);
    setUploadFileProps((uploadFileProps) => ({
      ...uploadFileProps,
      renderUploadedFiles: displayUploadedFiles(updatedFileList),
      errors: [],
      key: Date.now()
    }));
  };

  const renderFileUpload = () => {
    return (
      <div className="upload_document_area">
        <div className="upload_container">
          <div className="progress_bar">
            <div
              className="progress_line"
              style={{
                width: `${progressValue}%`,
                background: '#cc000a',
                height: '4px',
                borderRadius: 10
              }}
            ></div>
          </div>
          <FileUpload optionalProps={uploadFileProps?.accept} onClick={sendAnalytics} onChange={onUploadChange} {...uploadFileProps} />
          <div class="uploadedFiles">{uploadFileProps?.renderUploadedFiles}</div>
        </div>
      </div>
    );
  };

  return isDrawerModal ? (
    <div className="config_content">
      <h5>{heading}</h5>
      <p>{subHeading}</p>
      {renderFileUpload()}
    </div>
  ) : (
    renderFileUpload()
  );
};

export default UploadComponent;
